import { useState, useRef, useEffect, useCallback, useMemo } from "react";

function lsGet(key, fallback) {
  try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ─── SAMPLE JSON ──────────────────────────────────────────────────────────────
const SAMPLE = `[
  { "id": 1, "name": "Alice Chen", "email": "alice@example.com", "role": "Engineer", "department": "Platform", "salary": 120000, "active": true, "joined": "2021-03-15" },
  { "id": 2, "name": "Bob Martinez", "email": "bob@example.com", "role": "Designer", "department": "Product", "salary": 95000, "active": true, "joined": "2022-07-01" },
  { "id": 3, "name": "Clara Thompson", "email": "clara@example.com", "role": "Manager", "department": "Platform", "salary": 140000, "active": false, "joined": "2019-11-20" }
]`;

const SAMPLE_DIFF_A = `{
  "name": "Alice",
  "age": 28,
  "role": "Engineer",
  "active": true
}`;

const SAMPLE_DIFF_B = `{
  "name": "Alice",
  "age": 29,
  "role": "Senior Engineer",
  "active": true,
  "department": "Platform"
}`;

// ─── TOOLS CONFIG ─────────────────────────────────────────────────────────────
const TOOLS = [
  { id: "formatter",  label: "Formatter",   icon: "✨", desc: "Beautify & format JSON" },
  { id: "validator",  label: "Validator",   icon: "✓",  desc: "Validate JSON syntax" },
  { id: "to-csv",     label: "→ CSV",       icon: "⇄",  desc: "Convert JSON to CSV" },
  { id: "to-xml",     label: "→ XML",       icon: "〈〉", desc: "Convert JSON to XML" },
  { id: "to-yaml",    label: "→ YAML",      icon: "≡",  desc: "Convert JSON to YAML" },
  { id: "minifier",   label: "Minifier",    icon: "⊡",  desc: "Minify / compress JSON" },
  { id: "diff",       label: "Diff",        icon: "⊕",  desc: "Compare two JSON objects" },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function tryParse(text) {
  try { return { data: JSON.parse(text.trim()), error: null }; }
  catch (e) { return { data: null, error: e.message }; }
}

function flattenObj(obj, prefix = "") {
  return Object.keys(obj).reduce((acc, k) => {
    const key = prefix ? `${prefix}.${k}` : k;
    const val = obj[k];
    if (val !== null && typeof val === "object" && !Array.isArray(val))
      Object.assign(acc, flattenObj(val, key));
    else acc[key] = val;
    return acc;
  }, {});
}

// JSON → CSV
function jsonToCsv(data, delimiter = ",", includeHeader = true, quoteAll = false, flatten = true) {
  const rows = Array.isArray(data) ? data : [data];
  const processed = rows.map(r => flatten && typeof r === "object" ? flattenObj(r) : r);
  const cols = [...new Set(processed.flatMap(r => Object.keys(r || {})))];
  const d = delimiter === "tab" ? "\t" : delimiter;
  const esc = v => {
    const s = v === null || v === undefined ? "" : String(v);
    return quoteAll || s.includes(d) || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [];
  if (includeHeader) lines.push(cols.map(esc).join(d));
  for (const row of processed) lines.push(cols.map(c => esc((row || {})[c])).join(d));
  return { output: lines.join("\n"), cols };
}

// JSON → XML
function jsonToXml(data, rootName = "root", itemName = "item", indent = 2) {
  const pad = (n) => " ".repeat(n);
  function convert(val, key, depth) {
    const tag = key || "item";
    const safe = tag.replace(/[^a-zA-Z0-9_\-:.]/g, "_").replace(/^([0-9])/, "_$1");
    if (val === null || val === undefined)  return `${pad(depth)}<${safe}/>`;
    if (typeof val !== "object")            return `${pad(depth)}<${safe}>${String(val).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</${safe}>`;
    if (Array.isArray(val)) {
      return val.map(item => convert(item, safe, depth)).join("\n");
    }
    const children = Object.entries(val).map(([k, v]) => convert(v, k, depth + indent)).join("\n");
    return `${pad(depth)}<${safe}>\n${children}\n${pad(depth)}</${safe}>`;
  }
  if (Array.isArray(data)) {
    const items = data.map(item => convert(item, itemName, indent)).join("\n");
    return `<?xml version="1.0" encoding="UTF-8"?>\n<${rootName}>\n${items}\n</${rootName}>`;
  }
  return `<?xml version="1.0" encoding="UTF-8"?>\n${convert(data, rootName, 0)}`;
}

// JSON → YAML
function jsonToYaml(data, indent = 2, depth = 0) {
  const pad = " ".repeat(depth);
  if (data === null)              return "null";
  if (data === undefined)         return "~";
  if (typeof data === "boolean")  return data ? "true" : "false";
  if (typeof data === "number")   return String(data);
  if (typeof data === "string") {
    if (/[\n:#{}&*!|>'"%@`]/.test(data) || data.trim() !== data || data === "")
      return `"${data.replace(/\\/g,"\\\\").replace(/"/g,'\\"')}"`;
    return data;
  }
  if (Array.isArray(data)) {
    if (data.length === 0) return "[]";
    return data.map(item => {
      const val = jsonToYaml(item, indent, depth + indent);
      return `${pad}- ${val.trimStart()}`;
    }).join("\n");
  }
  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (keys.length === 0) return "{}";
    return keys.map(k => {
      const val = data[k];
      const safeKey = /[\s:#{}[\],&*?|<>=!%@`]/.test(k) ? `"${k}"` : k;
      if (val !== null && typeof val === "object") {
        return `${pad}${safeKey}:\n${jsonToYaml(val, indent, depth + indent)}`;
      }
      return `${pad}${safeKey}: ${jsonToYaml(val, indent, depth)}`;
    }).join("\n");
  }
  return String(data);
}

// JSON Diff
function diffObjects(a, b, path = "") {
  const diffs = [];
  const allKeys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of allKeys) {
    const fullPath = path ? `${path}.${key}` : key;
    if (!(key in (a || {}))) {
      diffs.push({ type: "added",   path: fullPath, bVal: b[key] });
    } else if (!(key in (b || {}))) {
      diffs.push({ type: "removed", path: fullPath, aVal: a[key] });
    } else if (typeof a[key] === "object" && a[key] !== null && typeof b[key] === "object" && b[key] !== null && !Array.isArray(a[key]) && !Array.isArray(b[key])) {
      diffs.push(...diffObjects(a[key], b[key], fullPath));
    } else if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      diffs.push({ type: "changed", path: fullPath, aVal: a[key], bVal: b[key] });
    }
  }
  return diffs;
}

// ─── SHARED EDITOR COMPONENT ─────────────────────────────────────────────────
function JsonEditor({ value, onChange, placeholder, label, status, t, height = 280, readOnly = false }) {
  const lines = value.split("\n").length;
  return (
    <div style={{ display:"flex", flexDirection:"column", height }}>
      {label && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color: status === "error" ? t.statusErr : status === "ok" ? t.statusOk : t.textAccent }}>
          <span>{label}</span>
          {status === "ok"    && <span>✓ Valid JSON</span>}
          {status === "error" && <span>✕ Invalid JSON</span>}
          <span style={{ color:t.textMuted, fontWeight:400 }}>{lines} lines</span>
        </div>
      )}
      <textarea
        value={value}
        onChange={e => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        spellCheck={false}
        style={{
          flex:1, width:"100%", background:"transparent", border:"none", outline:"none",
          padding:"12px 14px", color: readOnly ? t.textSecondary : t.textPrimary,
          fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75,
          resize:"none", tabSize:2,
        }}
      />
    </div>
  );
}

// ─── COPY BUTTON ─────────────────────────────────────────────────────────────
function CopyBtn({ text, t }) {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); });
  return (
    <button onClick={copy} className={`btn-secondary ${copied ? "success" : ""}`} style={{ padding:"5px 14px", fontSize:12 }}>
      {copied ? "✓ Copied" : "⎘ Copy"}
    </button>
  );
}

function downloadText(content, filename, mime = "text/plain") {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

// ─── CELL VALUE ──────────────────────────────────────────────────────────────
function CellValue({ value }) {
  if (value === null || value === undefined) return <span style={{ opacity:0.4, fontStyle:"italic" }}>null</span>;
  if (typeof value === "boolean") return <span style={{ color: value ? "#22c55e" : "#ef4444" }}>{String(value)}</span>;
  if (typeof value === "number")  return <span style={{ opacity:0.85 }}>{value}</span>;
  return <>{String(value)}</>;
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" });
}

// ─────────────────────────────────────────────────────────────────────────────
// TOOL PANELS
// ─────────────────────────────────────────────────────────────────────────────

// ── 1. FORMATTER / BEAUTIFIER ────────────────────────────────────────────────
function Formatter({ t }) {
  const [input, setInput]   = useState(SAMPLE);
  const [indent, setIndent] = useState(2);
  const { data, error } = useMemo(() => tryParse(input), [input]);

  const formatted = useMemo(() => {
    if (!data) return "";
    return JSON.stringify(data, null, indent);
  }, [data, indent]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontSize:12, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Indent spaces:</span>
        {[2, 4, 8].map(n => (
          <button key={n} className="btn-sm" onClick={() => setIndent(n)}
            style={{ borderColor: indent===n ? t.borderActive : t.border, color: indent===n ? t.textAccent : t.textMuted, background: indent===n ? t.bgChipActive : "none" }}>
            {n}
          </button>
        ))}
        <button className="btn-sm" onClick={() => setInput(SAMPLE)}>Load Sample</button>
        <button className="btn-sm" onClick={() => setInput("")}>Clear</button>
      </div>
      <div className="grid-2">
        <div className="panel" style={{ border:`1px solid ${error ? t.borderError : input.trim() ? t.borderActive : t.border}` }}>
          <JsonEditor value={input} onChange={setInput} placeholder="Paste JSON here…" label="Input" status={input.trim() ? (error ? "error" : "ok") : ""} t={t} />
          {error && <div style={{ padding:"7px 14px", fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono", borderTop:`1px solid ${t.border}` }}>✕ {error}</div>}
        </div>
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Formatted Output</span>
            <div style={{ display:"flex", gap:6 }}>
              {formatted && <CopyBtn text={formatted} t={t} />}
              {formatted && <button className="btn-sm" onClick={() => downloadText(formatted, "formatted.json", "application/json")}>↓ Download</button>}
            </div>
          </div>
          <JsonEditor value={formatted} onChange={() => {}} readOnly t={t} placeholder="Formatted JSON appears here…" />
        </div>
      </div>
    </div>
  );
}

// ── 2. VALIDATOR ─────────────────────────────────────────────────────────────
function Validator({ t }) {
  const [input, setInput] = useState(SAMPLE);
  const { data, error }   = useMemo(() => tryParse(input), [input]);

  const stats = useMemo(() => {
    if (!data) return null;
    const arr = Array.isArray(data) ? data : [data];
    const keys = new Set(arr.flatMap(r => typeof r === "object" && r ? Object.keys(r) : []));
    const depth = (obj, d = 0) => {
      if (typeof obj !== "object" || obj === null) return d;
      return Math.max(...Object.values(obj).map(v => depth(v, d + 1)), d);
    };
    return {
      type:    Array.isArray(data) ? "Array" : "Object",
      items:   Array.isArray(data) ? data.length : 1,
      keys:    keys.size,
      depth:   depth(data),
      size:    new Blob([input]).size,
      nulls:   JSON.stringify(data).split("null").length - 1,
    };
  }, [data, input]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8 }}>
        <button className="btn-sm" onClick={() => setInput(SAMPLE)}>Load Sample</button>
        <button className="btn-sm" onClick={() => setInput("")}>Clear</button>
      </div>

      {/* Status banner */}
      {input.trim() && (
        <div style={{ background: error ? t.borderError : t.pillGreen, border:`1px solid ${error ? t.statusErr : t.statusOk}`, borderRadius:10, padding:"14px 18px", display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:28 }}>{error ? "✕" : "✓"}</span>
          <div>
            <div style={{ fontSize:15, fontWeight:600, color: error ? t.statusErr : t.statusOk }}>
              {error ? "Invalid JSON" : "Valid JSON"}
            </div>
            <div style={{ fontSize:12, color: error ? t.statusErr : t.statusOk, opacity:0.8, fontFamily:"JetBrains Mono" }}>
              {error || "Your JSON is well-formed and valid"}
            </div>
          </div>
        </div>
      )}

      <div className="grid-2" style={{ alignItems:"start" }}>
        <div className="panel" style={{ border:`1px solid ${error ? t.borderError : input.trim() ? t.borderActive : t.border}` }}>
          <JsonEditor value={input} onChange={setInput} placeholder="Paste JSON to validate…" label="JSON Input" status={input.trim() ? (error ? "error" : "ok") : ""} t={t} height={340} />
        </div>

        {/* Stats panel */}
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {stats && !error && [
            { label:"Type",       val: stats.type },
            { label:"Items",      val: stats.items },
            { label:"Unique Keys",val: stats.keys },
            { label:"Max Depth",  val: stats.depth },
            { label:"File Size",  val: stats.size > 1024 ? `${(stats.size/1024).toFixed(1)} KB` : `${stats.size} B` },
            { label:"Null Values",val: stats.nulls },
          ].map(({ label, val }) => (
            <div key={label} style={{ background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, color:t.textSecondary }}>{label}</span>
              <span style={{ fontSize:14, fontWeight:600, color:t.textAccent, fontFamily:"JetBrains Mono" }}>{val}</span>
            </div>
          ))}
          {!input.trim() && (
            <div className="panel" style={{ padding:24, textAlign:"center", color:t.textMuted, fontSize:13 }}>
              Paste JSON on the left to see statistics
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 3. JSON → CSV ─────────────────────────────────────────────────────────────
function JsonToCsvTool({ t, onExport }) {
  const [input, setInput]               = useState(SAMPLE);
  const [delimiter, setDelimiter]       = useState(",");
  const [includeHeader, setIncludeHeader] = useState(true);
  const [quoteAll, setQuoteAll]         = useState(false);
  const [flatten, setFlatten]           = useState(true);
  const [sortCol, setSortCol]           = useState(null);
  const [sortDir, setSortDir]           = useState("asc");
  const [tableSearch, setTableSearch]   = useState("");
  const [selectedCols, setSelectedCols] = useState(null);
  const [colSearch, setColSearch]       = useState("");
  const [filterOpen, setFilterOpen]     = useState(true);
  const [copied, setCopied]             = useState(false);
  const fileRef = useRef(null);
  const [dragOver, setDragOver]         = useState(false);

  const { data, error } = useMemo(() => tryParse(input), [input]);
  const rows = useMemo(() => data ? (Array.isArray(data) ? data : [data]) : [], [data]);
  const allCols = useMemo(() => rows.length ? [...new Set(rows.flatMap(r => Object.keys(flatten ? flattenObj(r) : r)))] : [], [rows, flatten]);

  useEffect(() => {
    if (allCols.length && selectedCols === null) setSelectedCols(new Set(allCols));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCols.length]);

  const activeCols = useMemo(() => selectedCols ? allCols.filter(c => selectedCols.has(c)) : allCols, [selectedCols, allCols]);

  let processedRows = useMemo(() => rows.map(r => flatten ? flattenObj(r) : r), [rows, flatten]);
  if (tableSearch.trim()) {
    const q = tableSearch.toLowerCase();
    processedRows = processedRows.filter(row => activeCols.some(c => String(row[c] ?? "").toLowerCase().includes(q)));
  }
  if (sortCol) {
    processedRows = [...processedRows].sort((a, b) => {
      const cmp = String(a[sortCol] ?? "").localeCompare(String(b[sortCol] ?? ""), undefined, { numeric:true });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }

  const { output: csv } = useMemo(() => {
    if (!data || !activeCols.length) return { output:"" };
    return jsonToCsv(data, delimiter, includeHeader, quoteAll, flatten);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, delimiter, includeHeader, quoteAll, flatten, activeCols.length]);

  const isReady = !!data && activeCols.length > 0;
  const filteredCols = allCols.filter(c => c.toLowerCase().includes(colSearch.toLowerCase()));
  const toggleCol = col => setSelectedCols(prev => { const n = new Set(prev); n.has(col) ? (n.size > 1 && n.delete(col)) : n.add(col); return n; });

  const download = () => {
    if (!isReady) return;
    downloadText(csv, "export.csv", "text/csv");
    onExport && onExport({ tool:"JSON→CSV", rows: rows.length, cols: activeCols.length, csv, timestamp: Date.now() });
  };

  const handleFile = file => {
    if (!file) return;
    const r = new FileReader(); r.onload = e => { setInput(e.target.result); setSelectedCols(null); }; r.readAsText(file);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {/* Drop zone */}
      <div style={{ border:`2px dashed ${dragOver ? t.borderActive : t.dragBorder}`, borderRadius:12, background:dragOver ? t.bgChipActive : t.dragBg, padding:"1rem 1.5rem", textAlign:"center", cursor:"pointer", transition:"all 0.2s", color: dragOver ? t.textAccent : t.dragText, fontSize:13 }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}>
        <div style={{ fontSize:20, marginBottom:4 }}>⬆</div>
        Drop a <strong style={{ color:t.textAccent }}>JSON file</strong> or <strong style={{ color:t.textAccent }}>click to browse</strong>
        <input ref={fileRef} type="file" accept=".json" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])} />
      </div>

      <div className="grid-2">
        {/* Input */}
        <div className="panel" style={{ border:`1px solid ${error ? t.borderError : input.trim() ? t.borderActive : t.border}` }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color: error ? t.statusErr : input.trim() ? t.textAccent : t.textMuted }}>JSON Input</span>
            <div style={{ display:"flex", gap:6 }}>
              <button className="btn-sm" onClick={() => { setInput(SAMPLE); setSelectedCols(null); }}>Sample</button>
              <button className="btn-sm" onClick={() => { setInput(""); setSelectedCols(null); }}>Clear</button>
            </div>
          </div>
          <JsonEditor value={input} onChange={v => { setInput(v); setSelectedCols(null); }} placeholder={'[\n  { "id": 1, "name": "Alice" }\n]'} t={t} height={260} />
          <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, fontFamily:"JetBrains Mono", color: error ? t.statusErr : data ? t.statusOk : t.textMuted }}>
            {error ? `✕ ${error.slice(0,55)}` : data ? `✓ ${rows.length} rows · ${allCols.length} columns` : "Paste JSON above"}
          </div>
        </div>

        {/* Preview */}
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Preview</span>
            {isReady && <span style={{ fontSize:11, color:t.textMuted, fontFamily:"JetBrains Mono" }}>{activeCols.length}/{allCols.length} cols</span>}
          </div>
          {isReady && (
            <div style={{ padding:"7px 12px", borderBottom:`1px solid ${t.border}`, background:t.bgPanelHead }}>
              <input value={tableSearch} onChange={e => setTableSearch(e.target.value)} placeholder="Search rows…"
                style={{ width:"100%", background:t.bgSearchInp, border:`1px solid ${t.border}`, borderRadius:7, padding:"4px 10px", fontSize:11, color:t.textPrimary, fontFamily:"JetBrains Mono", outline:"none" }} />
            </div>
          )}
          {isReady ? (
            <>
              <div className="table-wrap" style={{ maxHeight:200 }}>
                <table className="data-table">
                  <thead><tr>{activeCols.map(c => <th key={c} onClick={() => { if(sortCol===c) setSortDir(d=>d==="asc"?"desc":"asc"); else{setSortCol(c);setSortDir("asc");} }} style={{ cursor:"pointer" }}>{c}{sortCol===c?(sortDir==="asc"?" ↑":" ↓"):""}</th>)}</tr></thead>
                  <tbody>{processedRows.slice(0,50).map((row,i) => <tr key={i}>{activeCols.map(c=><td key={c}><CellValue value={row[c]}/></td>)}</tr>)}</tbody>
                </table>
              </div>
              <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, fontFamily:"JetBrains Mono", color:t.statusOk }}>{processedRows.length} rows{tableSearch ? ` (filtered from ${rows.length})` : ""}</div>
            </>
          ) : (
            <div className="empty-state"><div className="empty-icon-box">⊞</div><span>Preview appears here</span></div>
          )}
        </div>
      </div>

      {/* Column filter */}
      {isReady && (
        <div className="panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 14px", cursor:"pointer" }} onClick={() => setFilterOpen(v=>!v)}>
            <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
              <div className="dot dot-blue"/>Choose Columns <span style={{ color:t.textMuted, fontWeight:400 }}>{activeCols.length} of {allCols.length}</span>
            </div>
            <span style={{ color:t.chevron, fontSize:12, transform: filterOpen?"rotate(180deg)":"none", transition:"transform 0.25s" }}>▾</span>
          </div>
          {filterOpen && (
            <div className="slide-down" style={{ padding:"10px 14px 14px", borderTop:`1px solid ${t.border}` }}>
              <div style={{ display:"flex", gap:7, marginBottom:9, flexWrap:"wrap", alignItems:"center" }}>
                <input value={colSearch} onChange={e=>setColSearch(e.target.value)} placeholder="Search columns…"
                  style={{ flex:1, minWidth:120, background:t.bgSearchInp, border:`1px solid ${t.border}`, borderRadius:7, padding:"4px 10px", fontSize:11, color:t.textPrimary, fontFamily:"JetBrains Mono", outline:"none" }} />
                <button className="btn-sm" onClick={() => setSelectedCols(new Set(allCols))}>All</button>
                <button className="btn-sm" onClick={() => { if(allCols.length) setSelectedCols(new Set([allCols[0]])); }}>None</button>
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {filteredCols.map(col => { const active = selectedCols?.has(col); return (
                  <div key={col} className={`chip ${active?"active":""}`} onClick={() => toggleCol(col)} style={{ fontSize:11 }}>
                    {active && <div className="check">✓</div>}{col}
                  </div>
                ); })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings + download */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Separator</span>
          <select value={delimiter} onChange={e=>setDelimiter(e.target.value)} style={{ background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"2px 7px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textAccent, outline:"none" }}>
            <option value=",">, comma</option><option value=";">; semicolon</option><option value="|">| pipe</option><option value="tab">→ tab</option>
          </select>
        </div>
        {[{label:"Include Header",val:includeHeader,fn:()=>setIncludeHeader(v=>!v)},{label:"Quote All",val:quoteAll,fn:()=>setQuoteAll(v=>!v)},{label:"Flatten Nested",val:flatten,fn:()=>{setFlatten(v=>!v);setSelectedCols(null);}}].map(({label,val,fn})=>(
          <div key={label} onClick={fn} style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer" }}>
            <div className={`toggle ${val?"on":""}`}><div className="toggle-thumb"/></div>
            <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>{label}</span>
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <button className="btn-primary" onClick={download} disabled={!isReady}>↓ Download CSV</button>
        <button className={`btn-secondary ${copied?"success":""}`} onClick={() => { navigator.clipboard.writeText(csv).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),1600);}); }} disabled={!isReady}>
          {copied ? "✓ Copied!" : "⎘ Copy CSV"}
        </button>
        {isReady && <div style={{ flex:1, minWidth:0, background:t.bgCsvPrev, border:`1px solid ${t.border}`, borderRadius:10, padding:"8px 12px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textMuted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{csv.split("\n").slice(0,2).join(" ↵ ").slice(0,90)}…</div>}
      </div>
    </div>
  );
}

// ── 4. JSON → XML ─────────────────────────────────────────────────────────────
function JsonToXmlTool({ t }) {
  const [input, setInput]       = useState(SAMPLE);
  const [rootName, setRootName] = useState("root");
  const [itemName, setItemName] = useState("item");
  const [indentX, setIndentX]   = useState(2);
  const { data, error } = useMemo(() => tryParse(input), [input]);
  const xml = useMemo(() => data ? jsonToXml(data, rootName, itemName, indentX) : "", [data, rootName, itemName, indentX]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        {[{label:"Root tag", val:rootName, set:setRootName, w:110},{label:"Item tag", val:itemName, set:setItemName, w:110}].map(({label,val,set,w})=>(
          <div key={label} style={{ display:"flex", alignItems:"center", gap:6, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
            <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>{label}</span>
            <input value={val} onChange={e=>set(e.target.value)} style={{ width:w, background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:6, padding:"3px 8px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none" }} />
          </div>
        ))}
        <div style={{ display:"flex", alignItems:"center", gap:6, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Indent</span>
          {[2,4].map(n=><button key={n} className="btn-sm" onClick={()=>setIndentX(n)} style={{ borderColor:indentX===n?t.borderActive:t.border, color:indentX===n?t.textAccent:t.textMuted, background:indentX===n?t.bgChipActive:"none" }}>{n}</button>)}
        </div>
        <button className="btn-sm" onClick={()=>setInput(SAMPLE)}>Sample</button>
        <button className="btn-sm" onClick={()=>setInput("")}>Clear</button>
      </div>
      <div className="grid-2">
        <div className="panel" style={{ border:`1px solid ${error?t.borderError:input.trim()?t.borderActive:t.border}` }}>
          <JsonEditor value={input} onChange={setInput} label="JSON Input" status={input.trim()?(error?"error":"ok"):""} t={t} placeholder="Paste JSON here…" />
          {error && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {error}</div>}
        </div>
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>XML Output</span>
            <div style={{ display:"flex", gap:6 }}>
              {xml && <CopyBtn text={xml} t={t}/>}
              {xml && <button className="btn-sm" onClick={()=>downloadText(xml,"output.xml","application/xml")}>↓ Download</button>}
            </div>
          </div>
          <JsonEditor value={xml} onChange={()=>{}} readOnly t={t} placeholder="XML appears here…"/>
        </div>
      </div>
    </div>
  );
}

// ── 5. JSON → YAML ────────────────────────────────────────────────────────────
function JsonToYamlTool({ t }) {
  const [input, setInput]   = useState(SAMPLE);
  const [indentY, setIndentY] = useState(2);
  const { data, error } = useMemo(() => tryParse(input), [input]);
  const yaml = useMemo(() => data ? jsonToYaml(data, indentY) : "", [data, indentY]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Indent</span>
          {[2,4].map(n=><button key={n} className="btn-sm" onClick={()=>setIndentY(n)} style={{ borderColor:indentY===n?t.borderActive:t.border, color:indentY===n?t.textAccent:t.textMuted, background:indentY===n?t.bgChipActive:"none" }}>{n}</button>)}
        </div>
        <button className="btn-sm" onClick={()=>setInput(SAMPLE)}>Sample</button>
        <button className="btn-sm" onClick={()=>setInput("")}>Clear</button>
      </div>
      <div className="grid-2">
        <div className="panel" style={{ border:`1px solid ${error?t.borderError:input.trim()?t.borderActive:t.border}` }}>
          <JsonEditor value={input} onChange={setInput} label="JSON Input" status={input.trim()?(error?"error":"ok"):""} t={t} placeholder="Paste JSON here…"/>
          {error && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {error}</div>}
        </div>
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>YAML Output</span>
            <div style={{ display:"flex", gap:6 }}>
              {yaml && <CopyBtn text={yaml} t={t}/>}
              {yaml && <button className="btn-sm" onClick={()=>downloadText(yaml,"output.yaml","text/yaml")}>↓ Download</button>}
            </div>
          </div>
          <JsonEditor value={yaml} onChange={()=>{}} readOnly t={t} placeholder="YAML appears here…"/>
        </div>
      </div>
    </div>
  );
}

// ── 6. MINIFIER ───────────────────────────────────────────────────────────────
function Minifier({ t }) {
  const [input, setInput] = useState(SAMPLE);
  const { data, error } = useMemo(() => tryParse(input), [input]);
  const minified = useMemo(() => data ? JSON.stringify(data) : "", [data]);
  const savings  = useMemo(() => {
    if (!input.trim() || !minified) return null;
    const orig = new Blob([input]).size;
    const min  = new Blob([minified]).size;
    return { orig, min, saved: orig - min, pct: Math.round((1 - min/orig)*100) };
  }, [input, minified]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8 }}>
        <button className="btn-sm" onClick={()=>setInput(SAMPLE)}>Load Sample</button>
        <button className="btn-sm" onClick={()=>setInput("")}>Clear</button>
      </div>

      {savings && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
          {[
            { label:"Original Size", val: savings.orig > 1024 ? `${(savings.orig/1024).toFixed(1)} KB` : `${savings.orig} B` },
            { label:"Minified Size", val: savings.min > 1024  ? `${(savings.min/1024).toFixed(1)} KB`  : `${savings.min} B` },
            { label:"Bytes Saved",   val: savings.saved > 1024 ? `${(savings.saved/1024).toFixed(1)} KB` : `${savings.saved} B` },
            { label:"Compression",   val: `${savings.pct}%` },
          ].map(({ label, val }) => (
            <div key={label} style={{ background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"12px 16px", textAlign:"center" }}>
              <div style={{ fontSize:11, color:t.textMuted, marginBottom:4, textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</div>
              <div style={{ fontSize:18, fontWeight:700, color:t.textAccent, fontFamily:"JetBrains Mono" }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid-2">
        <div className="panel" style={{ border:`1px solid ${error?t.borderError:input.trim()?t.borderActive:t.border}` }}>
          <JsonEditor value={input} onChange={setInput} label="Input (Beautified)" status={input.trim()?(error?"error":"ok"):""} t={t} placeholder="Paste JSON here…"/>
          {error && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {error}</div>}
        </div>
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Minified Output</span>
            <div style={{ display:"flex", gap:6 }}>
              {minified && <CopyBtn text={minified} t={t}/>}
              {minified && <button className="btn-sm" onClick={()=>downloadText(minified,"minified.json","application/json")}>↓ Download</button>}
            </div>
          </div>
          <JsonEditor value={minified} onChange={()=>{}} readOnly t={t} placeholder="Minified JSON appears here…"/>
        </div>
      </div>
    </div>
  );
}

// ── 7. DIFF CHECKER ───────────────────────────────────────────────────────────
function DiffChecker({ t }) {
  const [left, setLeft]   = useState(SAMPLE_DIFF_A);
  const [right, setRight] = useState(SAMPLE_DIFF_B);
  const { data: dataA, error: errA } = useMemo(() => tryParse(left), [left]);
  const { data: dataB, error: errB } = useMemo(() => tryParse(right), [right]);

  const diffs = useMemo(() => {
    if (!dataA || !dataB) return [];
    const a = Array.isArray(dataA) ? dataA[0] : dataA;
    const b = Array.isArray(dataB) ? dataB[0] : dataB;
    return diffObjects(a, b);
  }, [dataA, dataB]);

  const added   = diffs.filter(d => d.type === "added");
  const removed = diffs.filter(d => d.type === "removed");
  const changed = diffs.filter(d => d.type === "changed");
  const identical = !errA && !errB && dataA && dataB && diffs.length === 0;

  const diffTypeColor = { added: t.statusOk, removed: t.statusErr, changed: t.dotAmber };
  const diffTypeBg    = { added: t.pillGreen, removed: t.pillRed, changed: t.pillAmber };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8 }}>
        <button className="btn-sm" onClick={() => { setLeft(SAMPLE_DIFF_A); setRight(SAMPLE_DIFF_B); }}>Load Sample</button>
        <button className="btn-sm" onClick={() => { setLeft(""); setRight(""); }}>Clear</button>
      </div>

      <div className="grid-2">
        <div className="panel" style={{ border:`1px solid ${errA?t.borderError:left.trim()?t.borderActive:t.border}` }}>
          <JsonEditor value={left} onChange={setLeft} label="JSON A (Original)" status={left.trim()?(errA?"error":"ok"):""} t={t} placeholder="Paste first JSON…" height={260}/>
          {errA && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {errA}</div>}
        </div>
        <div className="panel" style={{ border:`1px solid ${errB?t.borderError:right.trim()?t.borderActive:t.border}` }}>
          <JsonEditor value={right} onChange={setRight} label="JSON B (Modified)" status={right.trim()?(errB?"error":"ok"):""} t={t} placeholder="Paste second JSON…" height={260}/>
          {errB && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {errB}</div>}
        </div>
      </div>

      {/* Diff results */}
      {dataA && dataB && (
        <div>
          {/* Summary */}
          <div style={{ display:"flex", gap:10, marginBottom:12, flexWrap:"wrap" }}>
            {identical ? (
              <div style={{ background:t.pillGreen, border:`1px solid ${t.statusOk}`, borderRadius:10, padding:"10px 20px", color:t.statusOk, fontSize:14, fontWeight:600 }}>
                ✓ Identical — no differences found
              </div>
            ) : (
              <>
                {[{label:`${added.length} Added`, color:t.statusOk, bg:t.pillGreen, border:t.statusOk},{label:`${removed.length} Removed`, color:t.statusErr, bg:t.pillRed, border:t.statusErr},{label:`${changed.length} Changed`, color:t.dotAmber, bg:t.pillAmber, border:t.dotAmber}].map(s=>(
                  <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.border}`, borderRadius:10, padding:"10px 18px", color:s.color, fontSize:13, fontWeight:600 }}>{s.label}</div>
                ))}
              </>
            )}
          </div>

          {/* Diff list */}
          {diffs.length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {diffs.map((d, i) => (
                <div key={i} style={{ background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"10px 14px", display:"grid", gridTemplateColumns:"auto 1fr", gap:"8px 16px", alignItems:"center" }}>
                  <span style={{ background:diffTypeBg[d.type], color:diffTypeColor[d.type], border:`1px solid ${diffTypeColor[d.type]}`, borderRadius:6, padding:"2px 9px", fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>{d.type}</span>
                  <div style={{ fontFamily:"JetBrains Mono", fontSize:12 }}>
                    <span style={{ color:t.textAccent, fontWeight:600 }}>{d.path}</span>
                    {d.type === "changed" && (
                      <span style={{ color:t.textMuted }}>
                        {" "}→{" "}<span style={{ color:t.statusErr, textDecoration:"line-through" }}>{JSON.stringify(d.aVal)}</span>
                        {" "}<span style={{ color:t.statusOk }}>{JSON.stringify(d.bVal)}</span>
                      </span>
                    )}
                    {d.type === "added"   && <span style={{ color:t.statusOk }}>{" "}= {JSON.stringify(d.bVal)}</span>}
                    {d.type === "removed" && <span style={{ color:t.statusErr }}>{" "}= {JSON.stringify(d.aVal)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN JSON TOOLKIT ───────────────────────────────────────────────────────
const PAGE_TABS = ["Converter", "History"];

export default function JsonToolkit({ t, onExport }) {
  const [activeTool, setActiveTool] = useState("formatter");
  const [activeTab, setActiveTab]   = useState("Converter");
  const [history, setHistory]       = useState(() => lsGet("mdt-json-history", []));

  const handleExport = useCallback((entry) => {
    setHistory(h => {
      const next = [{ ...entry, id: Date.now() }, ...h].slice(0, 30);
      // Strip raw output data before persisting — only store metadata (tool, rows, cols, timestamp)
      const lean = next.map(({ csv: _c, xml: _x, yaml: _y, json: _j, ...rest }) => rest);
      lsSet("mdt-json-history", lean);
      return next; // full entry stays in React state for this session only
    });
    onExport && onExport(entry);
  }, [onExport]);


  return (
    <div>
      {/* Hero */}
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>{ {formatter:"✨",validator:"✓","to-csv":"⇄","to-xml":"〈〉","to-yaml":"≡",minifier:"⊡",diff:"⊕"}[activeTool]} JSON Toolkit</h2>
            <p>7 powerful JSON tools in one place — format, validate, convert, minify, and diff JSON instantly.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">7</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat"><div className="tool-stat-val">{history.length}</div><div className="tool-stat-lbl">exports</div></div>
            <div className="tool-stat"><div className="tool-stat-val">0</div><div className="tool-stat-lbl">server calls</div></div>
          </div>
        </div>
      </div>

      {/* Sub-tool selector */}
      <div className="pill-tab-bar" style={{ display:"flex", gap:6, padding:"12px 16px", flexWrap:"wrap" }}>
        {TOOLS.map(tool => {
          const active = activeTool === tool.id;
          return (
            <button key={tool.id} onClick={() => setActiveTool(tool.id)}
              className={`pill-tab ${active ? "active" : ""}`}>
              <span style={{ fontSize:12 }}>{tool.icon}</span>{tool.label}
            </button>
          );
        })}
        {/* separator then history */}
        <div style={{ marginLeft:"auto", display:"flex", gap:6, paddingRight:4 }}>
          {PAGE_TABS.map(tab => (
            <button key={tab} className={`pill-tab ${activeTab===tab ? "active" : ""}`} onClick={() => setActiveTab(tab)} style={{ fontSize:11 }}>
              {tab === "History" && history.length > 0 && <span style={{ background:"rgba(255,255,255,0.25)", color:"inherit", borderRadius:10, padding:"1px 6px", fontSize:9 }}>{history.length}</span>}
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tool content */}
      {activeTab === "Converter" && (
        <div className="tool-main">
          {activeTool === "formatter" && <Formatter t={t}/>}
          {activeTool === "validator" && <Validator t={t}/>}
          {activeTool === "to-csv"    && <JsonToCsvTool t={t} onExport={handleExport}/>}
          {activeTool === "to-xml"    && <JsonToXmlTool t={t}/>}
          {activeTool === "to-yaml"   && <JsonToYamlTool t={t}/>}
          {activeTool === "minifier"  && <Minifier t={t}/>}
          {activeTool === "diff"      && <DiffChecker t={t}/>}
        </div>
      )}

      {/* History tab */}
      {activeTab === "History" && (
        <div className="tool-main">
          {history.length === 0 ? (
            <div style={{ textAlign:"center", padding:"4rem 1rem", color:t.textMuted }}>
              <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
              <div style={{ fontSize:14, marginBottom:6 }}>No exports yet</div>
              <div style={{ fontSize:12 }}>Downloads from JSON → CSV will appear here</div>
              <button className="btn-sm" style={{ marginTop:16 }} onClick={() => { setActiveTab("Converter"); setActiveTool("to-csv"); }}>← Go to CSV tool</button>
            </div>
          ) : (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" }}>
                <div style={{ fontSize:13, color:t.textSecondary }}><span style={{ color:t.textAccent, fontWeight:600 }}>{history.length}</span> exports this session</div>
                <button className="btn-sm" onClick={() => { setHistory([]); lsSet("mdt-json-history", []); }}>Clear All</button>
              </div>
              <div style={{ display:"grid", gap:8 }}>
                {history.map(item => (
                  <div key={item.id} className="panel" style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:t.bgChipActive, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>📄</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:t.textPrimary, marginBottom:3 }}>{item.tool}</div>
                      <div style={{ fontSize:11, color:t.textMuted, fontFamily:"JetBrains Mono" }}>{item.rows} rows · {item.cols} columns · {formatTime(item.timestamp)}</div>
                    </div>
                    <button className="btn-sm" onClick={() => item.csv && downloadText(item.csv, "export.csv", "text/csv")}>↓ Re-download</button>
                    <button className="btn-sm" onClick={() => setHistory(h => { const next = h.filter(x=>x.id!==item.id); lsSet("mdt-json-history", next); return next; })} style={{ color:t.statusErr, borderColor:t.statusErr }}>✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <article className="tool-seo-section" aria-label="About JSON Toolkit">
        <h2>About JSON Toolkit</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>Formatter beautifies JSON with adjustable indent (2–8 spaces).</li>
              <li>Validator parses JSON and shows statistics: item count, key count, nesting depth, and file size.</li>
              <li>CSV conversion flattens nested objects with dot notation and supports column filtering.</li>
              <li>All 7 tools run entirely in your browser — no data leaves your device.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Format and inspect API responses during development.</li>
              <li>Convert JSON data exports to CSV for spreadsheet analysis.</li>
              <li>Minify JSON for production payloads to reduce transfer size.</li>
              <li>Compare two API versions with the JSON Diff tool.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>JSON is processed entirely in your browser.</li>
              <li>No data is sent to any server at any time.</li>
              <li>Free to use with no sign-up required.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>How do I convert JSON to CSV?</summary><p>Open the JSON Toolkit and click the "→ CSV" tab. Paste your JSON array, preview it as a table, optionally filter which columns to include, choose your delimiter (comma, semicolon, tab, pipe), and click "Download CSV". Nested objects are flattened with dot notation.</p></details>
        <details><summary>What does the JSON Diff tool show?</summary><p>The JSON Diff tool compares two JSON objects side by side. Added keys are shown in green, removed keys in red, and changed values show both old and new values. It operates on the structural level — comparing keys and values rather than raw text.</p></details>
        <details><summary>Can I convert JSON to XML or YAML?</summary><p>Yes. Use the "→ XML" tab to convert JSON to XML with configurable root and item tag names and indent. Use the "→ YAML" tab to generate properly indented YAML output with correct data-type handling.</p></details>
        <details><summary>Does the JSON formatter work offline?</summary><p>Yes. All 7 JSON tools run entirely in your browser with JavaScript — no server calls are made. After the first page load, the JSON Toolkit works without an internet connection.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>JSONFormatter.org</strong> — miniDevToolkit adds JSON diff, CSV/XML/YAML conversion, nested-object flattening, and offline support. Compared to <strong>Transform.tools</strong> — miniDevToolkit adds validation statistics, diff, and a history panel. Compared to <strong>CyberChef</strong> — miniDevToolkit has a purpose-built JSON UI with previews and column filtering instead of a generic recipe system. All tools share the same strict client-side privacy guarantee: your JSON is never sent to any server.</p>
      </article>
    </div>
  );
}